import { catalogHashService, type CatalogHashKind } from '../services/catalogHashService';
import { desktopProxyUrl } from '../utils/desktopRuntime';
import { hasServerCredentials } from '../utils/bootstrapPolicy';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://backend.auroraflux.net';

export class BackendError extends Error {
  constructor(readonly status: number, message: string) { super(message); }
}

export type LicenseStatus =
  | 'bootstrapping'
  | 'pending_activation'
  | 'active'
  | 'expired'
  | 'revoked';

export interface BootstrapResponse {
  device: {
    id: string;
    primaryDeviceId: string;
    activationDisplayId?: string;
    activationPassword: string;
    status: 'active' | 'blocked';
  };
  licenseStatus: LicenseStatus;
}

export interface ActivationCheckResponse {
  deviceId: string;
  activationDisplayId?: string;
  activationPassword?: string;
  licenseStatus: LicenseStatus;
  isActivated: boolean;
  expiresAt: number | null;
}

async function post<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(desktopProxyUrl(`${BACKEND_URL}${path}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new BackendError(res.status, `Backend ${res.status}: ${text}`);
  }
  let json;
  try { json = await res.json(); } catch { throw new BackendError(422, 'Invalid backend response'); }
  if (!json || json.success !== true || !('data' in json)) throw new BackendError(422, 'Invalid backend response');
  return json.data as T;
}

export interface BootstrapEvidence {
  deviceId: string;
  platformDeviceId?: string;
  platformDeviceIdType?: 'tizen-duid' | 'webos-lgudid';
  /** Previously issued server code, when this installation already exists. */
  activationDisplayId?: string;
  /** Previously issued server password; never derive a new one from hardware. */
  activationPassword?: string;
  platform?: string;
  appVersion?: string;
}

export async function bootstrapDevice(evidence: BootstrapEvidence): Promise<BootstrapResponse> {
  const result = await post<BootstrapResponse>('/api/v1/devices/bootstrap', {
    ...evidence,
    platform: evidence.platform ?? 'web',
    appVersion: evidence.appVersion ?? '1.0.0',
  });
  if (!hasServerCredentials(result?.device)) throw new BackendError(422, 'Invalid bootstrap response');
  if (result.device.status === 'blocked') throw new BackendError(403, 'Device blocked by administrator');
  return result;
}

export async function checkActivation(deviceId: string): Promise<ActivationCheckResponse> {
  return post<ActivationCheckResponse>('/api/v1/activation/check', { deviceId });
}

export interface XtreamProfile {
  id: string;
  deviceId: string;
  origin: 'app' | 'website' | 'admin';
  displayName: string;
  xtreamUrl: string;
  xtreamUsername: string;
  xtreamPassword: string;
  status: 'active' | 'invalid';
  validatedAt: number;
  lastValidationStatus: 'ok' | 'failed';
  requiresResellerCode?: boolean;
  resellerCode?: string;
  resellerCodeId?: string;
  resellerCodeSnapshot?: string;
  watchAccountId?: string;
  resellerId?: string;
}

export interface ActiveProfileSelection {
  deviceId: string;
  activeProfileId: string;
  selectedAt: number;
  selectedBy: 'app' | 'website' | 'admin';
}

export interface DeviceProfilesResponse {
  profiles: XtreamProfile[];
  activeProfileSelection: ActiveProfileSelection | null;
  profilesVersion: number;
}

async function get<T>(path: string, params: Record<string, string>): Promise<T> {
  const qs = Object.keys(params).map(function(k) {
    return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
  }).join('&');
  const url = desktopProxyUrl(`${BACKEND_URL}${path}${qs ? '?' + qs : ''}`);
  let res: Response;
  let preparingPolls = 0;
  while (true) {
    res = await fetch(url);
    if (res.status !== 202) break;
    const payload = await res.json().catch(() => null) as { data?: { retryAfterMs?: number } } | null;
    preparingPolls += 1;
    if (preparingPolls >= 300) throw new Error('Backend catalog preparation timed out');
    const retryAfterMs = Math.min(5_000, Math.max(1_000, Number(payload?.data?.retryAfterMs || 2_000)));
    await new Promise((resolve) => setTimeout(resolve, retryAfterMs));
  }
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`Backend ${res.status}: ${text}`);
  }
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Backend error');
  return json.data as T;
}

async function usableCatalogHash(kind: CatalogHashKind): Promise<string> {
  const current = catalogHashService.getHash(kind);
  if (current) return current;
  return (await catalogHashService.ensure(kind)).hash;
}

export async function getDeviceProfiles(
  deviceId: string,
  activationPassword: string
): Promise<DeviceProfilesResponse> {
  return get<DeviceProfilesResponse>('/api/v1/profiles/device', {
    deviceId,
    activationPassword,
  });
}

// ---------------------------------------------------------------------------
// App config — periodic license verification settings
// ---------------------------------------------------------------------------

export interface LicenseVerificationSettings {
  intervalMs?: number;
  gracePeriodMs?: number;
  retryDelaysMs?: number[];
  maxRetryIntervalMs?: number;
  jitterRatio?: number;
}

export interface AppConfigResponse {
  activationWebsiteUrl?: string;
  playlistActivationUrl?: string;
  licenseVerificationSettings?: LicenseVerificationSettings;
  updatedAt?: number;
}

export async function getAppConfig(): Promise<AppConfigResponse> {
  return get<AppConfigResponse>('/api/v1/app-config', {});
}

// ---------------------------------------------------------------------------
// Xtream proxy token — short-lived Worker access token
// ---------------------------------------------------------------------------

export interface XtreamProxyTokenResponse {
  token: string;
  expiresAt: number;
  ttlSeconds: number;
}

export async function getXtreamProxyToken(
  deviceId: string,
  activationPassword: string
): Promise<XtreamProxyTokenResponse> {
  return post<XtreamProxyTokenResponse>('/api/v1/xtream-proxy/token', {
    deviceId,
    activationPassword,
  });
}

// ---------------------------------------------------------------------------
// Authenticated license verification
// ---------------------------------------------------------------------------

export interface LicenseVerificationResponse {
  licenseStatus: LicenseStatus;
  verifiedAt: number;
  expiresAt: number | null;
  playbackAllowed: boolean;
}

export async function verifyLicense(deviceId: string, activationPassword: string): Promise<LicenseVerificationResponse> {
  return post<LicenseVerificationResponse>('/api/v1/licenses/verify', { deviceId, activationPassword });
}

// ---------------------------------------------------------------------------
// App-only profile command
// ---------------------------------------------------------------------------

export interface ResetDeviceProfilesResponse {
  deletedProfileIds: string[];
  profilesVersion: number;
}

export async function resetDeviceProfiles(
  deviceId: string,
  activationPassword: string,
): Promise<ResetDeviceProfilesResponse> {
  return post<ResetDeviceProfilesResponse>('/api/v1/profiles/reset-device', {
    deviceId,
    activationPassword,
  });
}
// ---------------------------------------------------------------------------
// Watch Sync V2 — one authenticated, idempotent batch endpoint
// ---------------------------------------------------------------------------

export interface WatchProgressItem {
  streamId: string;
  streamType: 'movie' | 'series_episode';
  position: number;
  duration: number;
  completed: boolean;
  serverRevision: string;
  updatedAt: number;
}

export interface WatchHistoryItem {
  contentId: string;
  contentType: 'movie' | 'series';
  name: string;
  posterUrl?: string | null;
  backdropUrl?: string | null;
  streamId: string;
  lastEpisodeStreamId?: string | null;
  isKids?: boolean;
  serverRevision: string;
  updatedAt: number;
}

export interface WatchFavoriteItem {
  itemId: string;
  itemType: 'live' | 'movies' | 'series';
  name: string;
  icon?: string | null;
  categoryId?: string | null;
  streamId?: string | null;
  seriesId?: string | null;
  containerExtension?: string | null;
  isKids?: boolean;
  serverRevision: string;
  updatedAt: number;
}

export type WatchSyncV2Operation =
  | { operationId: string; type: 'progress.upsert'; payload: { streamId: string; streamType: string; position: number; duration: number; completed?: boolean } }
  | { operationId: string; type: 'progress.restart'; payload: { streamId: string; streamType: string; duration?: number } }
  | { operationId: string; type: 'progress.complete'; payload: { streamId: string; streamType: string; position?: number; duration?: number } }
  | { operationId: string; type: 'progress.delete'; payload: { streamId: string; streamType: string } }
  | { operationId: string; type: 'history.upsert'; payload: { contentId: string; contentType: string; name: string; posterUrl?: string | null; backdropUrl?: string | null; streamId: string; lastEpisodeStreamId?: string | null; isKids?: boolean } }
  | { operationId: string; type: 'history.delete'; payload: { contentId: string; contentType: string } }
  | { operationId: string; type: 'favorite.upsert'; payload: { itemId: string; itemType: string; name: string; icon?: string | null; categoryId?: string | null; streamId?: string | null; seriesId?: string | null; containerExtension?: string | null; isKids?: boolean } }
  | { operationId: string; type: 'favorite.delete'; payload: { itemId: string; itemType: string } }
  | { operationId: string; type: 'watch.clear'; payload: Record<string, never> };

export interface WatchSyncV2State {
  syncRevision: string;
  fullSnapshot: true;
  progress: WatchProgressItem[];
  history: WatchHistoryItem[];
  favorites: WatchFavoriteItem[];
}

export interface WatchSyncV2Response {
  profileId: string;
  requestedSinceRevision: string;
  confirmedOperations: Array<{
    operationId: string;
    serverRevision: string;
    replayed: boolean;
  }>;
  state: WatchSyncV2State;
}

export interface WatchSyncV2Request {
  deviceId: string;
  activationPassword: string;
  profileId: string;
  sinceRevision: string;
  operations: WatchSyncV2Operation[];
}

export async function syncWatchV2(input: WatchSyncV2Request): Promise<WatchSyncV2Response> {
  return post<WatchSyncV2Response>('/api/v2/watch/sync', input as unknown as Record<string, unknown>);
}

// ---------------------------------------------------------------------------
// Highlights — server-side TMDB+Xtream matching, cached per serverHost
// ---------------------------------------------------------------------------

export interface HighlightItem {
  tmdbId: number;
  title: string;
  posterUrl: string | null;
  backdropUrl: string | null;
  vodStreamId: string;
  releaseDate: string;
}

export async function getHighlights(
  deviceId: string,
  activationPassword: string,
  language: string,
  forceRefresh = false,
): Promise<HighlightItem[]> {
  const vodCatalogHash = await usableCatalogHash('vod');
  const params: Record<string, string> = {
    deviceId,
    activationPassword,
    language,
    vodCatalogHash,
  };
  if (forceRefresh) params.forceRefresh = '1';
  return get<HighlightItem[]>('/api/v1/tmdb/highlights', params);
}

// ---------------------------------------------------------------------------
// Matched List — server-side TMDB+Xtream batch matching per category
// ---------------------------------------------------------------------------

export interface MatchedItem {
  tmdbId: number;
  title: string;
  posterUrl: string | null;
  backdropUrl: string | null;
  streamId: string;
  streamType: 'movie' | 'series';
  categoryId?: string;
}

export async function getMatchedList(
  deviceId: string,
  activationPassword: string,
  category: string,
  language: string,
  region?: string,
  limit?: number,
  mode?: 'preview' | 'full',
  forceRefresh = false,
): Promise<MatchedItem[]> {
  const params: Record<string, string> = { deviceId, activationPassword, category, language };
  const seriesCategories = ['on-the-air', 'national-series', 'top-series', 'kids-series', 'kids-animated-series', 'kids-live-action-series', 'anime-series', 'popular-series'];
  if (seriesCategories.indexOf(category) >= 0) params.seriesCatalogHash = await usableCatalogHash('series');
  else params.vodCatalogHash = await usableCatalogHash('vod');
  if (region) params.region = region;
  if (limit !== undefined) params.limit = String(limit);
  if (mode) params.mode = mode;
  if (forceRefresh) params.forceRefresh = '1';
  return get<MatchedItem[]>('/api/v1/tmdb/matched-list', params);
}

export interface GlobalSearchItem {
  title: string;
  posterUrl: string | null;
  streamId: string;
  streamType: 'movie' | 'series' | 'live';
  categoryId?: string;
}

export interface GlobalSearchPayload {
  movies: GlobalSearchItem[];
  series: GlobalSearchItem[];
  live: GlobalSearchItem[];
  moviesTruncated: boolean;
  seriesTruncated: boolean;
  liveTruncated: boolean;
}

export async function getGlobalSearch(
  deviceId: string,
  activationPassword: string,
  query: string,
  forceRefresh = false,
): Promise<GlobalSearchPayload> {
  const [liveCatalogHash, vodCatalogHash, seriesCatalogHash] = await Promise.all([
    usableCatalogHash('live'), usableCatalogHash('vod'), usableCatalogHash('series'),
  ]);
  return get<GlobalSearchPayload>('/api/v1/tmdb/global-search', {
    deviceId, activationPassword, q: query, liveCatalogHash, vodCatalogHash, seriesCatalogHash,
    ...(forceRefresh ? { forceRefresh: '1' } : {}),
  });
}

export async function getCatalogAlternatives(
  deviceId: string,
  activationPassword: string,
  type: 'movie' | 'series',
  title: string,
  streamId: string,
  forceRefresh = false,
): Promise<GlobalSearchItem[]> {
  const kind: CatalogHashKind = type === 'movie' ? 'vod' : 'series';
  const hash = await usableCatalogHash(kind);
  return get<GlobalSearchItem[]>('/api/v1/tmdb/alternatives', {
    deviceId,
    activationPassword,
    type,
    title,
    streamId,
    ...(forceRefresh ? { forceRefresh: '1' } : {}),
    ...(kind === 'vod' ? { vodCatalogHash: hash } : { seriesCatalogHash: hash }),
  });
}

export async function getCatalogRecommendations(
  deviceId: string,
  activationPassword: string,
  input: {
    type: 'movie' | 'series';
    tmdbId?: number;
    title?: string;
    year?: string;
    plot?: string;
    language: string;
  },
  forceRefresh = false,
): Promise<MatchedItem[]> {
  const kind: CatalogHashKind = input.type === 'movie' ? 'vod' : 'series';
  const hash = await usableCatalogHash(kind);
  const params: Record<string, string> = {
    deviceId,
    activationPassword,
    type: input.type,
    language: input.language,
    title: input.title || '',
    year: input.year || '',
    plot: input.plot || '',
    ...(kind === 'vod' ? { vodCatalogHash: hash } : { seriesCatalogHash: hash }),
  };
  if (input.tmdbId) params.tmdbId = String(input.tmdbId);
  if (forceRefresh) params.forceRefresh = '1';
  return get<MatchedItem[]>('/api/v1/tmdb/recommendations', params);
}
